import { v4 as uuidv4 } from 'uuid';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { CONFIG } from '../config';
import { deepClean } from '../utils/runtimeValidation';
import { IndexedDBService } from '../utils/indexedDbService';
import { db as firestoreDB } from '../firebaseConfig';

const RESULTS_LOCAL_STORE = 'results';

export interface TestResult {
  id: string;
  sessionQuestions?: { id: string; answer: unknown; text?: string }[];
  demographics?: Record<string, unknown>;
  timestamp?: string;
  synced?: boolean;
  [key: string]: unknown;
}

export class ResultService {
  /**
   * Формирует финальный объект результата
   */
  static buildFinalResultObject(data: Partial<TestResult>): TestResult {
    const now = new Date();
    return {
      ...data,
      id: data.id || uuidv4(),
      timestamp: data.timestamp || now.toISOString(),
    } as TestResult;
  }

  /**
   * Валидация результата (заглушка, если нужно — добавить validateData)
   */
  static validate(result: TestResult): void {
    // validateData('result_test.schema.json', result);
  }

  /**
   * Сохраняет результат в IndexedDB и Firestore
   */
  static async saveResult<T extends TestResult>(result: T, options: { skipFirebase?: boolean } = {}): Promise<string> {
    const cleanResult = deepClean(result);
    this.validate(cleanResult);
    const id = cleanResult.id || uuidv4();
    const dataToSave = { ...cleanResult, id };
    // IndexedDB
    await IndexedDBService.put<T>(RESULTS_LOCAL_STORE, dataToSave);
    // Firestore
    if (!options.skipFirebase) {
      await setDoc(doc(firestoreDB, CONFIG.COLLECTIONS.RESULTS, id), dataToSave, { merge: true });
      // Обновить статус синхронизации
      await IndexedDBService.put<T>(RESULTS_LOCAL_STORE, { ...dataToSave, synced: true });
    }
    return id;
  }

  /**
   * Загружает результат по id
   */
  static async loadResult<T extends TestResult = TestResult>(id: string): Promise<T | null> {
    // Сначала из IndexedDB
    let result = await IndexedDBService.get<T>(RESULTS_LOCAL_STORE, id);
    if (result) return result;
    // Потом из Firestore
    const docRef = doc(firestoreDB, CONFIG.COLLECTIONS.RESULTS, id);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      result = { ...docSnap.data(), id: docSnap.id } as T;
      await IndexedDBService.put<T>(RESULTS_LOCAL_STORE, result);
      return result;
    }
    return null;
  }

  /**
   * Обновляет существующий результат
   */
  static async updateResult<T extends TestResult>(id: string, updates: Partial<T>): Promise<void> {
    const existing = await this.loadResult<T>(id);
    if (!existing) throw new Error(`Result with id ${id} not found`);
    const updated = this.buildFinalResultObject({ ...existing, ...updates, id });
    await this.saveResult(updated);
  }
}
